import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./anim";

const FAQS = [
  {
    q: "How much does consulting cost?",
    a: "Pricing varies based on your goals, your needs, and the level of consulting required. We discuss scope and pricing openly in your first consultation, before you commit to anything.",
  },
  {
    q: "Is this right for my situation?",
    a: "The first consultation exists to answer exactly that. If we are not the right fit for your goals, we will tell you directly and point you in a better direction.",
  },
  {
    q: "Are you licensed financial advisors?",
    a: "No. Kingdom Wealth Enterprises provides business and wealth-building consulting. We do not provide regulated investment advisory, securities, legal, tax, or accounting services, and we encourage you to consult licensed professionals where appropriate.",
  },
  {
    q: "How quickly will I see value?",
    a: "Every client is different, but most gain clarity and a strategic direction from the very first consultation.",
  },
  {
    q: "How much time will this take?",
    a: "Engagements are designed around your schedule. You set the pace, and we make every session count.",
  },
  {
    q: "Is my information confidential?",
    a: "Yes. Everything you share is held in strict confidence and used only to serve your engagement.",
  },
];

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`faq-item-${index + 1}`} className="border-b border-white/10">
      <button
        data-testid={`faq-question-${index + 1}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 py-6 text-left"
      >
        <span className={`text-base font-light tracking-tight transition-colors duration-300 sm:text-lg ${open ? "text-[#C5A059]" : "text-[#F9F8F6]"}`}>
          {item.q}
        </span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.35, ease: EASE }}>
          <Plus size={18} className="shrink-0 text-[#C5A059]" aria-hidden="true" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="overflow-hidden"
          >
            <p data-testid={`faq-answer-${index + 1}`} className="max-w-3xl pb-7 text-sm font-light leading-relaxed text-[#A1A1AA]">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Faq() {
  return (
    <section id="faq" data-testid="faq-section" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <div className="grid gap-14 lg:grid-cols-[1fr_1.6fr] lg:gap-24">
        <SectionHeading
          overline="FAQ"
          title="Questions worth asking before you commit."
          copy="Straight answers on cost, fit, scope, and confidentiality."
        />
        <Reveal delay={0.1}>
          <div className="border-t border-white/10">
            {FAQS.map((item, i) => (
              <FaqItem key={item.q} item={item} index={i} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
